import React, { useState } from 'react';
import type { ReactNode } from 'react';

import { Collapse, IconButton, Typography } from '@mui/material';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { makeStyles } from 'tss-react/mui';

import { BackgroundTaskTrayPortal } from './BackgroundTaskTray';

export const BACKGROUND_TASK_ICON_SIZE = 16;

interface BackgroundTaskPanelProps {
  title: string;
  /** Extra header buttons, placed before collapse and close. Use `BackgroundTaskPanelAction`. */
  actions?: ReactNode;
  children?: ReactNode;
  closeLabel?: string;
  /** Shown in the header only while the body is collapsed, such as a percentage. */
  collapsedHint?: ReactNode;
  count?: number;
  icon?: ReactNode;
  onClose?: () => void;
}

interface BackgroundTaskPanelActionProps {
  children: ReactNode;
  label: string;
  onClick: () => void;
}

const useStyles = makeStyles()(theme => ({
  '@keyframes panelEnter': {
    from: {
      opacity: 0,
      transform: 'translateY(12px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  actionButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
      color: theme.appColors.iconsType,
    },
    color: theme.appColors.secondaryType,
    padding: theme.spacing(0.5),
  },
  body: {
    borderTop: `1px solid ${theme.appColors.rule}`,
  },
  count: {
    backgroundColor: theme.appColors.darkBackground,
    borderRadius: theme.spacing(1),
    color: theme.appColors.secondaryType,
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    padding: theme.spacing(0.5, 1),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(1, 1, 1, 1.5),
  },
  hint: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
    fontSize: 12,
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
  },
  panel: {
    animation: '$panelEnter 0.2s ease-out',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: theme.spacing(1),
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    width: 380,
  },
  spacer: {
    flex: 1,
  },
  title: {
    flexShrink: 1,
    fontSize: 14,
    fontWeight: 600,
    minWidth: 0,
  },
}));

/**
 * A card in the background task tray: a header with the task's title and
 * controls, and a body the user can collapse. It renders into the tray from
 * wherever it is mounted, so the owner keeps the task's state and only
 * decides what the card says.
 */
export function BackgroundTaskPanel({
  actions,
  children,
  closeLabel = 'Close',
  collapsedHint,
  count,
  icon,
  onClose,
  title,
}: BackgroundTaskPanelProps): JSX.Element {
  const { classes } = useStyles();
  const [collapsed, setCollapsed] = useState(false);
  const hasBody = Boolean(children);

  return (
    <BackgroundTaskTrayPortal>
      <div className={classes.panel} role="status">
        <div className={classes.header}>
          {Boolean(icon) && <span className={classes.icon}>{icon}</span>}
          <Typography noWrap className={classes.title}>
            {title}
          </Typography>
          {count !== undefined && <span className={classes.count}>{count}</span>}
          <span className={classes.spacer} />
          {collapsed && Boolean(collapsedHint) && (
            <Typography className={classes.hint} component="span">
              {collapsedHint}
            </Typography>
          )}
          {actions}
          {hasBody && (
            <BackgroundTaskPanelAction
              label={collapsed ? 'Expand' : 'Minimize'}
              onClick={() => setCollapsed(current => !current)}
            >
              <CollapseIcon collapsed={collapsed} />
            </BackgroundTaskPanelAction>
          )}
          {onClose && (
            <BackgroundTaskPanelAction label={closeLabel} onClick={onClose}>
              <X size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
            </BackgroundTaskPanelAction>
          )}
        </div>
        {hasBody && (
          <Collapse in={!collapsed}>
            <div className={classes.body}>{children}</div>
          </Collapse>
        )}
      </div>
    </BackgroundTaskTrayPortal>
  );
}

/** An icon button for a panel header, styled and labelled like the panel's own controls. */
export function BackgroundTaskPanelAction({
  children,
  label,
  onClick,
}: BackgroundTaskPanelActionProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineTooltip arrow placement="top" title={label}>
      <IconButton
        aria-label={label}
        className={classes.actionButton}
        size="small"
        onClick={onClick}
      >
        {children}
      </IconButton>
    </AlineTooltip>
  );
}

function CollapseIcon({ collapsed }: { collapsed: boolean }): JSX.Element {
  if (collapsed) return <ChevronUp size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />;

  return <ChevronDown size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />;
}
